import React from 'react';
import SiteLayout, { PALETTE } from './SiteLayout';
import { CashelBlueCompactQuote } from './CashelBlueShowcase';
import SEO from '../../components/SEO';

const IMG_HERD = 'https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=2000&q=80';

export default function SiteAbout() {
  return (
    <SiteLayout>
      <SEO
        title="Why Keystone Exists · About CloudVoro"
        description="We believe food producers shouldn't need a six-figure ERP to prove their food is safe. CloudVoro (Ireland) built Keystone beside the vat at Cashel Farmhouse Cheesemakers."
        path="/site/about"
        keywords="CloudVoro, Keystone founders, why Keystone exists, Cashel Blue partnership, European food software, food ERP team, Asif Khan founder"
      />
      {/* HERO */}
      <section data-testid="site-about-hero" style={{ background: PALETTE.cream, padding: '160px 32px 100px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 20 }}>About CloudVoro · Why Keystone exists</div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(44px, 6vw, 78px)',
            fontWeight: 700, lineHeight: 0.98, letterSpacing: '-0.03em', color: PALETTE.navy, margin: 0, maxWidth: 980,
          }}>
            Built beside the vat,<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>not sold at you.</em>
          </h1>
          <p style={{ fontSize: 22, lineHeight: 1.5, color: PALETTE.slate, marginTop: 32, maxWidth: 720 }}>
            We believe the people who make Europe’s food shouldn’t need a six-figure ERP and a 12-month project to prove their food is safe. CloudVoro exists to build the alternative. Headquartered in Ireland, sister company to Intellix IT Solutions (UK).
          </p>
        </div>
      </section>

      {/* WIDE IMAGE */}
      <section style={{
        height: 420,
        backgroundImage: `url(${IMG_HERD})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }} />

      {/* FOUNDING CUSTOMER, Cashel Blue */}
      <section style={{ background: PALETTE.cream, padding: '80px 32px', borderBottom: `1px solid ${PALETTE.border}` }}>
        <div style={{
          maxWidth: 1080, margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr',
          gap: 56,
          alignItems: 'center',
        }} className="founding-grid">
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>
              Founding customer · Built with
            </div>
            <h2 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(28px, 3.8vw, 44px)', fontWeight: 700, color: PALETTE.navy,
              margin: 0, letterSpacing: '-0.02em', lineHeight: 1.1,
            }}>
              Side-by-side with<br/>
              <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>Cashel Farmhouse Cheesemakers.</em>
            </h2>
            <p style={{ fontSize: 16, lineHeight: 1.65, color: PALETTE.slate, marginTop: 20 }}>
              Keystone's first production deployment was at <strong style={{ color: PALETTE.navy, fontWeight: 600 }}>Cashel Blue</strong> in Fethard, Co. Tipperary, Ireland's most awarded farmhouse blue cheese. We worked directly with QA & Operations Manager <strong style={{ color: PALETTE.navy, fontWeight: 600 }}>Louis Clifton Brown</strong> and owners <strong style={{ color: PALETTE.navy, fontWeight: 600 }}>Sarah and Sergio Furno</strong> to map the workflow from milk intake to retailer dispatch.
            </p>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: PALETTE.slate, marginTop: 14 }}>
              The product wasn't designed for them, it was designed <em>with</em> them. Variable-weight batches, retailer code mapping, split-batch dispatch, AccountsIQ sync, use-by tracking, the things accounting packages couldn't do, and that generic ERPs needed €40k+ of customisation to attempt. Every founding-customer suggestion shipped within the same sprint cycle.
            </p>
            <div style={{
              marginTop: 24,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: PALETTE.muted,
            }}>
              <span style={{ width: 20, height: 1.5, background: PALETTE.gold }} />
              Active IT contract since 2024 · Live on Keystone since February 2026
            </div>
          </div>
          <CashelBlueCompactQuote />
        </div>
        <style>{`
          @media (max-width: 900px) { .founding-grid { grid-template-columns: 1fr !important; gap: 32px !important; } }
        `}</style>
      </section>

      {/* THE STORY */}
      <section style={{ background: PALETTE.paper, padding: '120px 32px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 80 }} className="about-grid">
          <div>
            <div style={{ position: 'sticky', top: 120 }}>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 12 }}>The story</div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, fontWeight: 700, color: PALETTE.navy, lineHeight: 1.1, letterSpacing: '-0.01em' }}>
                Why we exist.
              </div>
              <div style={{ marginTop: 20, fontSize: 14, color: PALETTE.muted }}>
                CloudVoro, founded in Ireland.<br/>15+ years in enterprise IT.<br/>Sister to <a href="https://www.intellixit.com/" target="_blank" rel="noopener noreferrer" style={{ color: PALETTE.navy, fontWeight: 600 }}>Intellix IT Solutions (UK)</a>.
              </div>
            </div>
          </div>
          <div style={{ fontSize: 18, lineHeight: 1.75, color: PALETTE.ink }}>
            <p style={{ marginTop: 0, fontSize: 22, fontFamily: "'Playfair Display', Georgia, serif", color: PALETTE.navy, fontStyle: 'italic', fontWeight: 500, lineHeight: 1.4 }}>
              Keystone didn’t start as a product idea. It started as a frustration that wouldn’t go away.
            </p>
            <p>
              Asif Khan spent 15+ years delivering enterprise IT, a multi-million-euro Microsoft Dynamics AX rollout for Kaspersky, SAP ECC implementations, secure infrastructure for defence and embassy estates. And in every food business he walked into, he found the same picture: brilliant operators running million-euro production on spreadsheets, being quoted €40k+ for ERPs that didn’t fit, and being told <em>“that’s just how it is.”</em>
            </p>
            <p>
              So we did the thing ERP vendors don’t do. We moved into a cheese house. Keystone was written at Cashel Farmhouse Cheesemakers in Tipperary, with the QA manager watching the screen, the dispatch lead asking awkward questions, and a Tuesday-morning order deadline closing in. It wasn’t designed for a market segment and then sold downwards. It was designed for real people on a real floor, then generalised.
            </p>
            <p>
              That origin is the whole strategy. Every feature in the product exists because a food manufacturer needed it that week, not because a roadmap workshop predicted it. And it’s why we tell prospects, in writing, when a competitor genuinely fits them better. Software built beside people behaves differently from software sold at them.
            </p>

            <div style={{ marginTop: 56, padding: 32, background: PALETTE.bgSoft, borderLeft: `4px solid ${PALETTE.magenta}` }} data-testid="about-beliefs">
              <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>What we believe</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 16.5, lineHeight: 1.6, color: PALETTE.ink }}>
                {[
                  'Traceability should take 30 seconds, not a weekend of spreadsheets.',
                  'A family food business shouldn\u2019t be one resignation away from losing its records.',
                  'Software for factories should be written in factories.',
                  'If a competitor genuinely fits you better, we should say so. We do, on our comparison pages.',
                ].map((b, i) => (
                  <li key={i} style={{ display: 'flex', gap: 12, padding: '7px 0' }}>
                    <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, color: PALETTE.magenta, flexShrink: 0 }}>0{i + 1}</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ marginTop: 32, padding: 32, background: PALETTE.navy }} data-testid="about-refusals">
              <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 14 }}>What we refuse to do</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 16, lineHeight: 1.6, color: '#E5E7EB' }}>
                {[
                  ['No sales funnel.', 'A direct line to the team that builds Keystone, senior people from the first call.'],
                  ['No hostage data.', 'Export everything, any time. Leaving must be easy, or staying means nothing.'],
                  ['No hidden pricing.', 'The number is on the pricing page.'],
                  ['No borrowed certifications.', 'What\u2019s a roadmap item is labelled a roadmap item.'],
                  ['No six-figure implementations.', 'Fixed fee, quoted in writing before you sign.'],
                ].map(([lead, rest], i) => (
                  <li key={i} style={{ padding: '7px 0', borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                    <strong style={{ color: '#fff', fontWeight: 700 }}>{lead}</strong> {rest}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) { .about-grid { grid-template-columns: 1fr !important; gap: 40px !important; } .about-grid > div:first-child > div { position: static !important; } }
        `}</style>
      </section>

      {/* CORE VALUES */}
      <section style={{ background: PALETTE.cream, padding: '120px 32px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>Our core values</div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(30px, 4vw, 46px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05 }}>
              Four commitments. No posters.
            </h2>
            <p style={{ fontSize: 17, color: PALETTE.slate, marginTop: 18, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}>
              Not framed on a wall. Visible in the product, and in writing on this site.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24 }} className="vals-grid">
            {[
              { t: 'Honest fit', d: 'If a full ERP genuinely suits you better, we say so and point you there. It\u2019s in writing on our comparison pages.' },
              { t: 'Built beside, not sold at', d: 'Features come from factory floors, not roadmap workshops. Founding-customer requests shipped in the same sprint.' },
              { t: 'Proof over adjectives', d: 'We publish measured trace times and real prices. 30-second traceability was timed at a working cheesemaker, not in a demo tenant.' },
              { t: 'Easy to leave', d: 'Your data exports cleanly, any time. Staying should be a choice, not a trap.' },
            ].map((v, i) => (
              <div key={v.t} style={{ background: PALETTE.paper, padding: 32, border: `1px solid ${PALETTE.border}` }}>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 12, color: PALETTE.magenta, fontWeight: 700, letterSpacing: '0.1em' }}>0{i + 1}</div>
                <h3 style={{ fontSize: 22, fontWeight: 700, color: PALETTE.navy, margin: '12px 0 10px' }}>{v.t}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.6, color: PALETTE.slate, margin: 0 }}>{v.d}</p>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) { .vals-grid { grid-template-columns: 1fr 1fr !important; } }
          @media (max-width: 500px) { .vals-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </section>

      {/* FOUNDER */}
      <section style={{ background: PALETTE.paper, padding: '80px 32px' }}>
        <div style={{ maxWidth: 920, margin: '0 auto', display: 'grid', gridTemplateColumns: '0.85fr 1.4fr', gap: 48, alignItems: 'center' }} className="founder-grid">
          <div style={{
            aspectRatio: '4/5',
            background: `linear-gradient(135deg, ${PALETTE.navy} 0%, ${PALETTE.navyMid} 100%)`,
            position: 'relative', overflow: 'hidden',
            maxWidth: 280,
          }}>
            <img
              src="/team/asif.jpg"
              alt="Asif Khan"
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: 'center top',
                filter: 'saturate(0.95) contrast(1.02)',
              }}
            />
            <div style={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(180deg, transparent 50%, rgba(6,26,46,0.85) 100%)`,
            }} />
            <div style={{ position: 'absolute', bottom: 24, left: 24, right: 24, color: '#fff', zIndex: 1 }}>
              <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold }}>Founder & Principal Consultant</div>
              <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>Asif Khan</div>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 12 }}>The founder</div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(26px, 3.2vw, 38px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              Asif Khan
            </h2>
            <div style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600, color: PALETTE.muted, marginTop: 10, lineHeight: 1.55 }}>
              Founder, Keystone & CloudVoro (Ireland)  ·  Director & Principal Consultant, Intellix IT Solutions (UK)  ·  MSc, Maynooth University
            </div>
            <p style={{ fontSize: 15.5, lineHeight: 1.65, color: PALETTE.slate, marginTop: 18 }}>
              Director & Principal Consultant at Intellix IT Solutions (UK), founder of CloudVoro (Ireland), and the person who built Keystone. Over the last 15+ years Asif has delivered enterprise-grade IT for clients you'd recognise, a multi-million-euro Microsoft Dynamics AX rollout for Kaspersky, SAP ECC implementations for several other enterprise clients, secure infrastructure for the Saudi Ministry of Defence and Royal Saudi Air Force, and the data-centre buildout at the Embassy of Indonesia in Riyadh.
            </p>
            <p style={{ fontSize: 15, lineHeight: 1.65, color: PALETTE.slate, marginTop: 14 }}>
              He's worked across hospitality, food manufacturing, healthcare, defence and equestrian, same playbook every time: cut the friction, harden the ops. Keystone exists because he kept watching brilliant food operators run on spreadsheets, paying €40k+ for ERPs that didn't fit, and being told <em>"that's just how it is."</em>
            </p>
            <p style={{ fontSize: 14.5, lineHeight: 1.65, color: PALETTE.slate, marginTop: 14 }}>
              You'll work with him and the senior engineering team directly. No sales funnel. No ticket queue. One number reaches the people who build Keystone.
            </p>
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.muted, marginBottom: 12 }}>Professional affiliations</div>
              <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                {[
                  { abbr: 'BCS', short: 'MBCS', title: 'Professional Member, British Computer Society (Chartered Institute for IT)', img: '/team/affiliations/bcs.jpg', pad: 8 },
                  { abbr: 'ICS', short: 'MICS', title: 'Member, Irish Computer Society', img: '/team/affiliations/ics.png', pad: 4 },
                  { abbr: 'ITAM', short: 'ITAM', title: 'IT Asset Manager (professional designation)' },
                  { abbr: 'ADPO', short: 'ADPO', title: 'Member, Association of Data Protection Officers (Ireland). Held for professional development; CloudVoro has not appointed an Art. 37 DPO.', img: '/team/affiliations/dpo.png', pad: 4 },
                  { abbr: 'IMCA', short: 'MIMCA', title: 'Member, Institute of Management Consultants and Advisors', img: '/team/affiliations/mimca.png', pad: 6 },
                  { abbr: 'HISI', short: 'MHISI', title: 'Member, Healthcare Informatics Society of Ireland', img: '/team/affiliations/hisi.png', pad: 4 },
                ].map(a => (
                  <li key={a.title} style={{
                    fontSize: 14, color: PALETTE.ink,
                    padding: '10px 0',
                    borderBottom: `1px solid ${PALETTE.border}`,
                    display: 'flex', gap: 14, alignItems: 'center',
                  }}>
                    <div style={{
                      width: 48, height: 48,
                      flexShrink: 0,
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: a.img ? '#fff' : `linear-gradient(135deg, ${PALETTE.navy} 0%, ${PALETTE.navyMid} 100%)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: a.img ? `1px solid ${PALETTE.border}` : 'none',
                      boxShadow: '0 1px 3px rgba(10,37,64,0.06)',
                      padding: a.img ? Math.max(2, (a.pad || 6) - 2) : 0,
                    }}>
                      {a.img ? (
                        <img
                          src={a.img}
                          alt={a.short}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <span style={{
                          fontFamily: "'Playfair Display', Georgia, serif",
                          fontSize: a.abbr.length > 3 ? 12 : 14,
                          fontWeight: 700,
                          color: PALETTE.gold,
                          letterSpacing: '0.04em',
                        }}>{a.abbr}</span>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, color: PALETTE.ink, lineHeight: 1.4 }}>
                        {a.title}
                      </div>
                      <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginTop: 2 }}>
                        {a.short}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 800px) { .founder-grid { grid-template-columns: 1fr !important; gap: 32px !important; } }
        `}</style>
      </section>

      {/* WIDER TEAM */}
      <section style={{ background: PALETTE.cream, padding: '72px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 56, alignItems: 'baseline', marginBottom: 40 }} className="wider-head">
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 10 }}>The wider team</div>
              <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(26px, 3.2vw, 38px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                People who <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>do the work.</em>
              </h2>
            </div>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: PALETTE.slate, margin: 0 }}>
              A senior team across engineering, integrations, design, compliance and client services. Small enough that you'll know everyone by first name within a fortnight.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 14,
            maxWidth: 1080,
          }} className="wider-tiles">
            {[
              { name: 'Dorottya Várkonyi', role: 'Data Protection & Legal Affairs Manager', img: '/team/dorottya.jpg', initials: 'DV', bio: 'Leads the legal function across the group. Contracts, policies, data protection and regulatory documentation.' },
              { name: 'Belma Arnautovic', role: 'Head of Sales & GTM', img: '/team/belma.jpg', initials: 'BA', bio: 'Global GTM leader. Builds pipeline, sales teams and go-to-market strategies that drive revenue growth.' },
              { name: 'Kate Konovets', role: 'Meta Growth Marketing Expert', img: '/team/kate.png', initials: 'KK', bio: '5 years of performance marketing across eCommerce, SaaS and apps. Manages ad budgets of up to $200K per month.' },
              { name: 'Sophie Hennessy', role: 'Creative Operations Lead', img: '/team/sophie.jpg', initials: 'SH', bio: 'Brand, content and creative ops. Keeps the work shipping and on-message.' },
              { name: 'Arijit C.', role: 'Senior Software Developer & Integrations Team Manager, Front End', img: '/team/arijit.png', initials: 'AC', bio: 'Front-end engineering lead across Keystone, Voro Core and bespoke client builds.' },
              { name: 'Marzena', role: 'Shopify, Data Validation & Marketing Lead', img: '/team/marzena.jpg', initials: 'M', bio: 'Shopify storefronts, marketing operations and meticulous data validation across client builds.' },
              { name: 'Neeraj C.', role: 'Software & Integrations Project Manager, Custom Builds', img: null, initials: 'NC', bio: 'Project management for bespoke client systems.' },
              { name: 'Hemant Kalwani', role: 'Digital Strategy & Performance Team Manager', img: '/team/hemant.jpg', initials: 'HK' },
              { name: 'Thaer AlShihabe', role: 'IT Sales Executive', img: '/team/thaer.jpg', initials: 'TA' },
              { name: 'Aamer Sharief', role: 'Software & Integrations Project Manager, Microsoft AX and SAP', img: null, initials: 'AS' },
              { name: 'Ieva Markevica', role: 'IT Sales Manager', img: null, initials: 'IM' },
            ].map((p) => (
              <article key={p.name} data-testid={`team-tile-${p.initials.toLowerCase()}`} style={{
                background: PALETTE.paper,
                border: `1px solid ${PALETTE.border}`,
                overflow: 'hidden',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
              }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 24px 50px rgba(10,37,64,0.10)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{
                  aspectRatio: '1/1',
                  background: p.img
                    ? PALETTE.bgSoft
                    : `linear-gradient(135deg, ${PALETTE.navy} 0%, ${PALETTE.navyMid} 100%)`,
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {p.img ? (
                    <img
                      src={p.img}
                      alt={p.name}
                      style={{
                        width: '100%', height: '100%',
                        objectFit: 'cover', objectPosition: 'center top',
                        filter: 'saturate(0.95)',
                      }}
                    />
                  ) : (
                    <>
                      <div style={{
                        position: 'absolute', inset: 0,
                        backgroundImage: `radial-gradient(circle at 25% 25%, ${PALETTE.gold}22 0%, transparent 55%), radial-gradient(circle at 75% 75%, ${PALETTE.magenta}11 0%, transparent 55%)`,
                      }} />
                      <span style={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontSize: 'clamp(48px, 6vw, 72px)',
                        fontWeight: 700,
                        color: PALETTE.gold,
                        letterSpacing: '-0.04em',
                        position: 'relative',
                        zIndex: 1,
                      }}>{p.initials}</span>
                    </>
                  )}
                </div>
                <div style={{ padding: '12px 14px 16px' }}>
                  <h3 style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: 14,
                    fontWeight: 700,
                    color: PALETTE.navy,
                    margin: 0,
                    letterSpacing: '-0.01em',
                    lineHeight: 1.2,
                  }}>
                    {p.name}
                  </h3>
                  <div style={{
                    fontSize: 9.5,
                    color: PALETTE.magenta,
                    fontWeight: 700,
                    marginTop: 5,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    lineHeight: 1.4,
                  }}>
                    {p.role}
                  </div>
                  {p.bio && (
                    <p style={{ fontSize: 12, lineHeight: 1.5, color: PALETTE.slate, margin: '8px 0 0' }}>{p.bio}</p>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
        <style>{`
          @media (max-width: 1000px) {
            .wider-head { grid-template-columns: 1fr !important; gap: 20px !important; }
            .wider-tiles { grid-template-columns: 1fr 1fr !important; gap: 16px !important; }
          }
          @media (max-width: 540px) {
            .wider-tiles { grid-template-columns: 1fr 1fr !important; }
          }
        `}</style>
      </section>
    </SiteLayout>
  );
}
